import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/**
 * Badge do Trakd · pill pra status, tags, contadores.
 *
 * Variants:
 *  - default  — gradient brand (pra destaque positivo)
 *  - violet   — violet sólido sutil
 *  - success  — verde sutil
 *  - warning  — amarelo sutil
 *  - error    — vermelho sutil
 *  - neutral  — surface (pra tags genéricas)
 *  - outline  — só borda
 *  - mono     — JetBrains Mono, uppercase, tracking (pra status codes)
 */
const badgeVariants = cva(
  [
    'inline-flex items-center justify-center gap-1 shrink-0',
    'rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
    'whitespace-nowrap transition-colors',
    '[&>svg]:size-3 [&>svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        default: 'bg-trakd-fill text-on-brand border-transparent shadow-trakd-glow-subtle',
        violet: 'bg-violet/15 text-violet border-violet/30',
        success: 'bg-success/15 text-success border-success/30',
        warning: 'bg-warning/15 text-warning border-warning/30',
        error: 'bg-error/15 text-error border-error/30',
        neutral: 'bg-surface-elevated/60 text-ink-muted border-border-subtle',
        outline: 'bg-transparent text-ink border-border',
        mono: [
          'bg-surface-elevated/60 text-ink-subtle border-transparent',
          'font-mono text-[10px] tracking-[0.12em] uppercase',
        ],
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, variant, ...props },
  ref,
) {
  return <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />;
});

export { badgeVariants };
